import {Copy, MapPin, MessageSquare, Share2} from "lucide-react";
import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {toast} from "@/src/modules/core/hooks/use-toast";
import {cn} from "@/lib/utils";

import {
  copyHouseholdAddress,
  formatHouseholdShareText,
  getGoogleMapsShareUrl,
  getSmsShareUrl,
  getWhatsAppShareUrl,
  isNativeShareAvailable,
  shareHouseholdAddressNative,
} from "../lib/share-household-address";

type ResidentHomeShareAddressButtonProps = {
  residentName: string;
  address: string;
  className?: string;
};

export function ResidentHomeShareAddressButton({
  residentName,
  address,
  className,
}: ResidentHomeShareAddressButtonProps) {
  const {t} = useTranslation("resident");
  const {t: tCommon} = useTranslation("common");
  const shareText = formatHouseholdShareText(residentName, address);
  const canNativeShare = isNativeShareAvailable(shareText);

  const handleNativeShare = async () => {
    const result = await shareHouseholdAddressNative(shareText);
    if (result === "failed") {
      toast.error(t("toast.share_options_error"));
    }
  };

  const handleCopy = async () => {
    const copied = await copyHouseholdAddress(shareText);
    if (copied) {
      toast.success(t("toast.address_copied"));
    } else {
      toast.error(t("toast.address_copy_error"));
    }
  };

  const openWhatsApp = () => {
    window.open(getWhatsAppShareUrl(shareText), "_blank", "noopener,noreferrer");
  };

  const openSms = () => {
    window.location.href = getSmsShareUrl(shareText);
  };

  const openGoogleMaps = () => {
    window.open(getGoogleMapsShareUrl(address), "_blank", "noopener,noreferrer");
  };

  return (
    <span className={cn("inline-flex align-middle", className)}>
      <DropdownMenu>
      <Tooltip>
        <TooltipTrigger
          render={
            <DropdownMenuTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground inline-flex size-7 shrink-0 p-0"
                  aria-label={t("share_address.aria_label")}
                />
              }
            />
          }
        >
          <Share2 className="size-3.5" aria-hidden />
        </TooltipTrigger>
        <TooltipContent>{t("share_address.tooltip")}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        {canNativeShare ? (
          <DropdownMenuItem onClick={() => void handleNativeShare()}>
            <Share2 className="size-4" />
            {t("share_address.native")}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onClick={openWhatsApp}>
          <MessageSquare className="size-4" />
          {t("share_address.whatsapp")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openSms}>
          <MessageSquare className="size-4" />
          {t("share_address.text_message")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openGoogleMaps}>
          <MapPin className="size-4" />
          {tCommon("open_in_google_maps")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void handleCopy()}>
          <Copy className="size-4" />
          {t("share_address.copy")}
        </DropdownMenuItem>
      </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
